import CryptoJS from "crypto-js";
import { v4 as uuidv4 } from "uuid";

const NEWLINE = "\n";
const SERVER_ROOT_SALT = "9sK@Lp2026#PrivateRootSalt_SecretSign";

export const SIGN_SKIP_URLS = ["/auth/open/login", "/anon/timestamp"];

/** 后端签名相关业务状态码 */
export const SIGNATURE_ERROR_STATUS = {
  SIGNATURE_REQUIRED: 1006,
  SIGNATURE_VERIFI_FAILED: 1007,
  REQUEST_INVALID_TIMEOUT: 1008,
  SIGNATURE_PARAM_LACK: 1009,
  REQUEST_INVALID_NONCE: 1010
} as const;

const SIGNATURE_ERROR_STATUS_SET = new Set<number>(
  Object.values(SIGNATURE_ERROR_STATUS)
);

const SIGNATURE_ERROR_FALLBACK: Record<number, string> = {
  [SIGNATURE_ERROR_STATUS.SIGNATURE_REQUIRED]: "签名不能为空！",
  [SIGNATURE_ERROR_STATUS.SIGNATURE_VERIFI_FAILED]: "签名校验失败！",
  [SIGNATURE_ERROR_STATUS.REQUEST_INVALID_TIMEOUT]: "请确认本地时间与网络时间同步",
  [SIGNATURE_ERROR_STATUS.SIGNATURE_PARAM_LACK]: "缺少必要的签名参数",
  [SIGNATURE_ERROR_STATUS.REQUEST_INVALID_NONCE]: "请求无效，重复请求！"
};

export function isSignatureErrorStatus(status: number): boolean {
  return SIGNATURE_ERROR_STATUS_SET.has(status);
}

/** 始终使用前端文案、忽略后端 message 的状态码（后端提示过于模糊） */
const FORCE_FRONTEND_MESSAGE_STATUS = new Set<number>([
  SIGNATURE_ERROR_STATUS.REQUEST_INVALID_TIMEOUT
]);

export function resolveSignatureErrorMessage(
  status: number,
  message?: string | null
): string {
  if (FORCE_FRONTEND_MESSAGE_STATUS.has(status)) {
    return SIGNATURE_ERROR_FALLBACK[status] || "请求失败";
  }
  const trimmed = message?.trim();
  if (trimmed) {
    return trimmed;
  }
  return SIGNATURE_ERROR_FALLBACK[status] || "请求失败";
}

/** 从响应体解析业务 status / message（兼容多层嵌套） */
export function extractBusinessResponseMeta(data: unknown): {
  status: number;
  message: string;
} {
  const pick = (obj: unknown): { status: number; message: string } | null => {
    if (!obj || typeof obj !== "object") {
      return null;
    }
    const record = obj as Record<string, unknown>;
    const rawStatus = record.status;
    if (rawStatus === undefined || rawStatus === null) {
      return null;
    }
    const status = Number(rawStatus);
    if (!Number.isFinite(status)) {
      return null;
    }
    const message =
      typeof record.message === "string"
        ? record.message
        : typeof record.msg === "string"
          ? record.msg
          : "";
    return { status, message };
  };

  const top = pick(data);
  if (top) {
    return top;
  }
  const nested = pick((data as Record<string, unknown>)?.data);
  if (nested) {
    return nested;
  }
  const level1 = (data as Record<string, unknown>)?.data;
  const level2 =
    level1 && typeof level1 === "object"
      ? (level1 as Record<string, unknown>).data
      : undefined;
  const deep = pick(level2);
  if (deep) {
    return deep;
  }
  return { status: NaN, message: "" };
}

export function shouldSkipSign(url?: string) {
  if (!url) return true;
  return SIGN_SKIP_URLS.some(item => url.includes(item));
}

function getRealEncryptKey(sessionKeyBase64: string) {
  const sessionKeyBin = CryptoJS.enc.Base64.parse(sessionKeyBase64);
  return CryptoJS.HmacSHA256(
    CryptoJS.enc.Utf8.parse(SERVER_ROOT_SALT),
    sessionKeyBin
  );
}

/** 是否与 axios 实际拼接到 URL 的 query 一致（空值不参与签名） */
function shouldIncludeQueryParam(val: unknown): boolean {
  if (val === undefined || val === null || val === "") {
    return false;
  }
  if (Array.isArray(val)) {
    return (
      val.length > 0 &&
      val[0] !== undefined &&
      val[0] !== null &&
      val[0] !== ""
    );
  }
  return true;
}

/** 过滤掉不会出现在 URL 上的空参数，保证签名与真实请求一致 */
export function sanitizeRequestParams(
  params?: Record<string, unknown> | null
): Record<string, unknown> | undefined {
  if (!params || typeof params !== "object") {
    return undefined;
  }
  const cleaned: Record<string, unknown> = {};
  for (const key of Object.keys(params)) {
    const val = params[key];
    if (shouldIncludeQueryParam(val)) {
      cleaned[key] = val;
    }
  }
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

/** 对齐 Java buildSortedQueryStr（仅含实际传递的 query 参数） */
export function buildSortedQueryStr(
  params?: Record<string, unknown> | null
): string {
  const sanitized = sanitizeRequestParams(params);
  if (!sanitized) {
    return "";
  }
  const sortedKeys = Object.keys(sanitized).sort();
  const parts: string[] = [];
  for (const key of sortedKeys) {
    const val = sanitized[key];
    if (Array.isArray(val)) {
      parts.push(`${key}=${val[0]}`);
    } else {
      parts.push(`${key}=${val}`);
    }
  }
  return parts.join("&");
}

/** 对齐 Java getRequestBody：签名与实际上传 body 须一致 */
export function buildBodyStr(data: unknown): string {
  if (data === undefined || data === null || data === "") {
    return "";
  }
  if (typeof data === "string") {
    return data;
  }
  if (data instanceof FormData || data instanceof URLSearchParams) {
    return "";
  }
  return JSON.stringify(data);
}

function buildSignString(options: {
  queryStr: string;
  bodyStr: string;
  uid: string;
  token: string;
  nonce: string;
  timestamp: string;
}) {
  return [
    options.queryStr,
    options.bodyStr,
    options.uid,
    options.token,
    options.nonce,
    options.timestamp
  ].join(NEWLINE);
}

export function createSign(options: {
  uid: string;
  token: string;
  nonce: string;
  timestamp: string;
  sessionKeyBase64: string;
  queryStr?: string;
  bodyStr?: string;
}): string {
  const signStr = buildSignString({
    queryStr: options.queryStr ?? "",
    bodyStr: options.bodyStr ?? "",
    uid: options.uid,
    token: options.token,
    nonce: options.nonce,
    timestamp: options.timestamp
  });
  const realKey = getRealEncryptKey(options.sessionKeyBase64);
  return CryptoJS.HmacSHA256(CryptoJS.enc.Utf8.parse(signStr), realKey)
    .toString(CryptoJS.enc.Hex)
    .toLowerCase();
}

export function generateNonce() {
  return uuidv4();
}

export function applyRequestSign(options: {
  uid: string;
  token: string;
  sessionKeyBase64: string;
  timestamp: string;
  params?: Record<string, unknown> | null;
  data: unknown;
  /** FormData 上传：bodyStr 取文件 MD5，与 X-File-Hash 一致 */
  fileHash?: string;
}) {
  const queryStr = buildSortedQueryStr(options.params);
  const bodyStr =
    options.fileHash !== undefined && options.fileHash !== ""
      ? options.fileHash
      : buildBodyStr(options.data);
  const nonce = generateNonce();
  const signStr = buildSignString({
    queryStr,
    bodyStr,
    uid: options.uid,
    token: options.token,
    nonce,
    timestamp: options.timestamp
  });
  const realKey = getRealEncryptKey(options.sessionKeyBase64);
  const sign = CryptoJS.HmacSHA256(CryptoJS.enc.Utf8.parse(signStr), realKey)
    .toString(CryptoJS.enc.Hex)
    .toLowerCase();
  return {
    queryStr,
    bodyStr,
    nonce,
    sign,
    signStr
  };
}
